import React from "react";
import { commitMutation, graphql } from "react-relay";

import { faEye } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faMask } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { memoize } from "lodash";
import moment from "moment";
import { ConnectionHandler, fetchQuery } from "relay-runtime";

import confettiAnimationData from "~/common/Animations/confetti.json";
import AppContext from "~/common/AppContext";
import { TeamsContext } from "~/integrations/teams/teamsContext";
import { ReactComponent as FellowLogo } from "~/legacy-assets/images/logos/fellow-icon-mono.svg";
import { inVideocall } from "~/lib/browserExtension";
import { confirm } from "~/lib/prompts";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { Fallbacks } from "~/lib/suspense";
import { inCalendarMode, inVideocallMode } from "~/lib/utils";
import { useGroupEmitter } from "~/lib/websocket";
import { environment } from "~/models";
import { Button, LoadingSpinner, toast } from "~/ui";
import Animation from "~/ui/Animation";
import NameList from "~/ui/avatar/NameList";
import { HelpCenterIcon, Modal } from "~/ui/legacyFellowIndex";
import { ToastActionButton } from "~/ui/toast";

import { FaceInput, type FaceValue, TextInput, ThumbInput, type ThumbValue } from "../FormInputs";
import { MAX_LENGTH } from "../FormInputs/TextInput";
import { adaptInputToQuestionChoice } from "../ResponseForm";
import { type Draft, isQuestionArray, isValidDraft, type Question } from "../ResponseForm/validation";

import type { MeetingFeedbackModalCreateMutation } from "~/__generated__/MeetingFeedbackModalCreateMutation.graphql";
import MeetingFeedbackModalQueryNode, {
	type MeetingFeedbackModalQuery as QueryType,
} from "~/__generated__/MeetingFeedbackModalQuery.graphql";

import styles from "./MeetingFeedbackModal.module.css";

type User = NonNullable<NonNullable<QueryType["response"]["note"]>["noteuserSet"]>[0]["user"];

function sendBrowserExtensionMessage(status: "requested" | "closed") {
	if (!inVideocall) {
		return;
	}

	window.parent.postMessage({ type: "FROM_MEETING_FEEDBACK", status }, "*");
}

const Query = graphql`
	query MeetingFeedbackModalQuery($streamId: ID!, $noteId: ID!) {
		stream: node(id: $streamId) {
			... on NoteStream {
				isMeetingFeedbackAnonymous
				meetingFeedbackTemplate {
					id
					template
				}
			}
		}

		note: node(id: $noteId) {
			... on Note {
				id
				title
				eventStart
				eventEnd
				finishedAt

				meetingFeedback {
					id
					isAnonymous
				}

				noteuserSet {
					user {
						__typename
						id
						email
						lastName
						firstName
						fullName
						isGuest
						isActive
					}
				}
			}
		}
	}
`;

const createMutation = graphql`
	mutation MeetingFeedbackModalCreateMutation($noteId: ID!, $contents: JSON!, $clientAnonymousStatus: Boolean!) {
		createMeetingFeedback(noteId: $noteId, contents: $contents, clientAnonymousStatus: $clientAnonymousStatus) {
			note {
				id
				viewerSentMeetingFeedback
				meetingFeedbackResponseCount
				stream {
					...OverviewSection_NoteStream
					isMeetingFeedbackTabEnabled
				}
			}

			feedback {
				id
				info
				isMeetingFeedback

				records {
					contents
				}

				context {
					__typename
					... on Note {
						eventStart
					}
				}

				...FeedbackCardFeedback
			}
		}
	}
`;

export function getNoteDateFormat(start?: string | null) {
	if (start) {
		const time = moment(start);

		if (time.isValid()) return time.calendar(undefined, { sameElse: "MMM Do YYYY, h:mm A" });
	}

	return "";
}

function getMeetingLengthInsight(eventEnd?: string | null, finishedAt?: string | null) {
	if (eventEnd && finishedAt) {
		const noteEndTime = moment(eventEnd);
		const noteFinishedAt = moment(finishedAt);

		if (noteEndTime.isValid() && noteFinishedAt.isValid()) {
			const diff = noteEndTime.diff(noteFinishedAt, "minutes");
			const mins = Math.abs(diff);

			if (mins !== 0) {
				return ` | Ended ${mins} minute${mins === 1 ? "" : "s"} ${diff > 0 ? "early 🎉" : "late 😐"}`;
			}
		}
	}

	return "";
}

function ResponseVisibility({ users, isAnonymous = false }: { users: User[]; isAnonymous?: boolean }) {
	const isPrivate = users.length === 1;
	const { user } = React.useContext(AppContext);

	return (
		<section className={styles.responseVisibility}>
			<p className={styles.visibilityText}>
				{isAnonymous ? (
					<FontAwesomeIcon icon={faMask} className={styles.icon} />
				) : (
					<FontAwesomeIcon icon={faEye} className={styles.icon} />
				)}
				{isPrivate ? (
					"This response will only be visible to you."
				) : (
					<>
						Response will be shared {isAnonymous && "(anonymously)"} with{" "}
						<NameList omitSelf capitalize selfId={user?.id} users={users} />
					</>
				)}
			</p>
		</section>
	);
}

export interface Props {
	close: () => void;
	streamId: string;
	noteId?: string;
	isPreview?: boolean;
	defaultValue?: FaceValue;
	onShowFeedbackContent?: (id: string) => void;
	useAnimation?: boolean;
	initiallyLeftMeeting?: boolean;
}

function MeetingFeedbackModal({
	close,
	streamId,
	onShowFeedbackContent,
	noteId,
	isPreview = false,
	defaultValue = "none",
	useAnimation = true,
	initiallyLeftMeeting = false,
}: Props) {
	// Ref for the thumbs question, so we can scroll to it when it becomes visible
	const thumbsSectionRef = React.useRef<HTMLDivElement>(null);

	const [leftMeeting, setLeftMeeting] = React.useState(initiallyLeftMeeting);

	React.useEffect(() => {
		// We need this to show the embedded version modal when the user left the call
		// but the non-embedded modal version was opened already during the call,
		// ex: "End meeting" button was clicked in the Note Header during the call.
		function onBrowserExtensionMessage(event: MessageEvent) {
			if (event.data.type === "BROWSER_EXTENSION_ACTION" && event.data.action === "rateMeeting" && !leftMeeting) {
				setLeftMeeting(true);
			}
		}

		if (inVideocall) {
			window.addEventListener("message", onBrowserExtensionMessage);
		}

		return () => window.removeEventListener("message", onBrowserExtensionMessage);
	}, [leftMeeting]);

	const { user } = React.useContext(AppContext);
	const teamsContext = React.useContext(TeamsContext);

	// We want to close the modal if feedback was given for this note in other device
	const emitter = useGroupEmitter(`user.${user?.id.replace(/=/g, "")}`);
	React.useEffect(() => {
		function onMessage(payload: { type: string; data: { noteId: string } }) {
			if (payload.type === "MEETING_FEEDBACK_SENT" && payload.data.noteId === noteId) {
				// store update is performed in the NoteHeader component
				close();
			}
		}

		emitter.on("message", onMessage);
		return () => emitter.off("message", onMessage);
	}, [emitter, noteId, close]);

	// the default null value makes TS type `props` as potentially null, which it otherwise wouldn't, but should
	const props = useLazyLoadQuery<QueryType>(Query, { streamId, noteId: noteId ?? "" }, { fetchPolicy: "network-only" });

	const clientAnonymousStatus =
		props.note?.meetingFeedback?.isAnonymous ?? props.stream?.isMeetingFeedbackAnonymous ?? true;

	/* Getting questions */
	const template = props.stream?.meetingFeedbackTemplate?.template;

	const questions = React.useMemo<readonly Question[]>(() => {
		if (isQuestionArray(template?.questions)) {
			return template.questions;
		}
		return [];
	}, [template]);

	/* Face response */
	const faceQuestion = questions[0];
	const [faceValue, setFaceValue] = React.useState<FaceValue>(defaultValue);
	const onFaceChange = React.useCallback(
		(value: FaceValue | null) => {
			if (value !== null) {
				setFaceValue(value);
				// We need to scroll to the thumbs section if it's not visible (in a timeout to wait for it to render)
				setTimeout(() => {
					if (thumbsSectionRef.current) {
						thumbsSectionRef.current.scrollIntoView({ behavior: "smooth" });
					}
				}, 0);
			}
		},
		[setFaceValue],
	);

	/* Thumbs responses */
	const thumbQuestions = React.useMemo(() => {
		return [questions[1], questions[2], questions[3]];
	}, [questions]);

	const [thumbValues, setThumbValues] = React.useState<ThumbValue[]>(["none", "none", "none"]);
	const onThumbsChange = React.useMemo(
		() =>
			memoize((index: number) => (value: ThumbValue | null) => {
				const newValues = [...thumbValues];
				newValues[index] = value || "none";
				setThumbValues(newValues);
			}),
		[thumbValues, setThumbValues],
	);

	/* Text response */
	const textQuestion = questions[4].type === "text" ? questions[4] : undefined;
	const [textValue, setTextValue] = React.useState("");
	const onTextChange = React.useCallback((value: string) => setTextValue(value), [setTextValue]);

	/* Send */
	const [loading, setLoading] = React.useState(false);

	const onSend = React.useCallback(() => {
		if (!noteId || loading) return;

		setLoading(true);

		const contents = questions.reduce((acc: Draft, q: Question) => {
			function getValue() {
				if (q.type === "choice" && q.id === faceQuestion.id) {
					return adaptInputToQuestionChoice(q, faceValue);
				} else if (q.type === "choice" && q.id === thumbQuestions[0].id) {
					return adaptInputToQuestionChoice(q, thumbValues[0]);
				} else if (q.type === "choice" && q.id === thumbQuestions[1].id) {
					return adaptInputToQuestionChoice(q, thumbValues[1]);
				} else if (q.type === "choice" && q.id === thumbQuestions[2].id) {
					return adaptInputToQuestionChoice(q, thumbValues[2]);
				} else if (q.id === textQuestion?.id && textValue.trim().length > 0) {
					return textValue.trim();
				}

				return undefined;
			}

			const value = getValue();
			return value ? [...acc, { id: q.id, value }] : acc;
		}, []);

		if (!isValidDraft(questions, contents)) {
			toast.error("Something went wrong trying to send these answers", { title: "Error" });
			return;
		}

		commitMutation<MeetingFeedbackModalCreateMutation>(environment, {
			mutation: createMutation,
			variables: { noteId, contents, clientAnonymousStatus },
			onCompleted: ({ createMeetingFeedback }, err) => {
				setLoading(false);

				if (err?.length || !createMeetingFeedback) {
					const errorMessage = err?.[0]?.message || "Something went wrong trying to save these answers";
					toast.error(errorMessage, { title: "Error" });
					if (errorMessage.includes("Inconsistent anonymous")) {
						fetchQuery(environment, MeetingFeedbackModalQueryNode, { streamId, noteId: noteId ?? "" }).toPromise();
					}
				} else {
					const hideButton = Boolean(
						user?.isGuest || inVideocallMode || inCalendarMode || Boolean(teamsContext?.isTeamsSidePanel),
					);

					const onClick = () => {
						onShowFeedbackContent?.(createMeetingFeedback.feedback.id);
					};

					close();
					sendBrowserExtensionMessage("closed");
					toast.success("Your feedback has been sent.", {
						// eslint-disable-next-line react/jsx-no-bind
						actions: hideButton ? undefined : <ToastActionButton onClick={onClick}>View responses</ToastActionButton>,
						closeOnClick: true,
						closeButton: true,
					});
				}
			},
			updater: store => {
				const newNode = store.getRootField("createMeetingFeedback")?.getLinkedRecord("feedback");

				if (!newNode) return;

				const stream = store.get(streamId);

				if (!stream) return;

				const connectionUpdater = (key: string) => {
					const connection = ConnectionHandler.getConnection(stream, key, {
						first: null,
						onlyMeetingFeedback: false,
					});

					if (!connection) return;

					const feedbackId = newNode.getValue("id");
					const nodes = (connection.getLinkedRecords("edges") ?? []).map(e => e.getLinkedRecord("node"));
					const oldNode = nodes.find(n => n?.getValue("id") === feedbackId);

					if (!oldNode) {
						// we need to create this edge
						// no cursor provided, insert the edge at the front
						const edge = ConnectionHandler.createEdge(store, connection, newNode, "NonNullFeedbackEdge");
						ConnectionHandler.insertEdgeBefore(connection, edge);
					}
				};

				connectionUpdater("stream_tabs_feedbackList"); // necessary to update the green dot in feedback tab
				connectionUpdater("stream_feedback_feedbackList"); // necessary to update the list of feedback cards in the feedback tab

				// else: if node existed, it will get updated automatically
			},
		});
	}, [
		questions,
		faceQuestion,
		faceValue,
		thumbQuestions,
		thumbValues,
		textQuestion,
		textValue,
		close,
		noteId,
		loading,
		setLoading,
		onShowFeedbackContent,
		streamId,
		user,
		teamsContext,
		clientAnonymousStatus,
	]);

	const formEdited = faceValue !== defaultValue || thumbValues.some(v => v !== "none") || textValue !== "";

	const onClose = React.useCallback(async () => {
		if (!isPreview && formEdited) {
			const result = await confirm("Changes you've made so far will not be saved.", {
				yesText: "Discard",
				noText: "Keep editing",
				simple: true,
				title: "Discard unsaved changes?",
			});

			if (result === true) {
				close();
				sendBrowserExtensionMessage("closed");
			}
		} else {
			close();
			sendBrowserExtensionMessage("closed");
		}
	}, [close, formEdited, isPreview]);

	React.useEffect(() => sendBrowserExtensionMessage("requested"), [leftMeeting]);

	return (
		<Modal
			onClose={onClose}
			data-meeting-feedback-modal
			width="25rem"
			className={cx({ [styles.postVideoCall]: leftMeeting })}
		>
			<Animation useAnimation={useAnimation} className={styles.animation} animationData={confettiAnimationData} />
			<Modal.Header className={styles.modalHeader}>
				{leftMeeting && <FellowLogo className={styles.fellowLogo} />}
				<Modal.Title className={styles.title}>{useAnimation ? "Meeting ended!" : "Give feedback"}</Modal.Title>
				{isPreview && <span className={styles.preview}>Preview</span>}
				<Modal.HeaderActions onClose={onClose}>
					{!leftMeeting && <HelpCenterIcon articleId="5318062" className={styles.helpLink} title="Meeting feedback" />}
				</Modal.HeaderActions>
			</Modal.Header>

			<Modal.Body className={cx(styles.modalBody, { [styles.extraPadding]: !isPreview && faceValue === "none" })}>
				{!isPreview && (
					<ResponseVisibility
						isAnonymous={clientAnonymousStatus}
						users={
							props.note?.noteuserSet?.map(({ user }) => user).filter(user => !user.isGuest && user.isActive) ?? []
						}
					/>
				)}

				{props.note && (
					<p className={styles.noteInfo}>
						{getNoteDateFormat(props.note.eventStart)}
						{getMeetingLengthInsight(props.note.eventEnd, props.note.finishedAt)}
					</p>
				)}

				<p className={styles.faceQuestion}>How was the {props.note?.title && <b>{props.note.title}</b>} meeting?</p>
				<FaceInput className={styles.faceInput} name={faceQuestion.id} value={faceValue} onChange={onFaceChange} />

				{(isPreview || faceValue !== "none") && (
					<>
						<p className={styles.thumbsQuestion} ref={thumbsSectionRef}>
							How would you rate the following?
						</p>

						{thumbQuestions.map((q, index) => (
							<div key={q.id} className={styles.thumbContainer}>
								<ThumbInput
									name={q.id}
									value={thumbValues[index]}
									onChange={onThumbsChange(index)}
									className={styles.thumbInput}
								/>
								<label className={styles.attribute}>{q.title}</label>
							</div>
						))}

						{textQuestion && (
							<div className={styles.textInputContainer}>
								<TextInput
									value={textValue}
									placeholder={textQuestion.title}
									onChange={onTextChange}
									readOnly={isPreview}
									answerType={textQuestion.appearance}
									maxLength={MAX_LENGTH[textQuestion.appearance]}
								/>
							</div>
						)}
					</>
				)}
			</Modal.Body>

			{!isPreview && (
				<Modal.Footer className={styles.footer}>
					<Button
						size="normal"
						variant="primary"
						onClick={onSend}
						disabled={loading || faceValue === "none"}
						data-meeting-feedback-modal-send-button
					>
						Send
					</Button>
				</Modal.Footer>
			)}
		</Modal>
	);
}

function ErrorFallback() {
	React.useEffect(() => {
		Sentry.captureException(new Error("MeetingFeedbackModal could not find Stream"));
	}, []);

	return null;
}

function LoadingFallback() {
	return <LoadingSpinner dimmer delay={1000} />;
}

export default function MeetingFeedbackModalWrapper(props: Props) {
	return (
		<Fallbacks loading={<LoadingFallback />} error={<ErrorFallback />}>
			<MeetingFeedbackModal {...props} />
		</Fallbacks>
	);
}
